// @ts-nocheck
import { useState } from 'react';
import { supabase } from '@product-radar/shared';
import { useAuth } from '@/contexts/auth-context';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Shield, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

interface MfaChallengeProps {
  onVerified: () => void;
}

export function MfaChallenge({ onVerified }: MfaChallengeProps) {
  const { setAuthState } = useAuth();
  const [code, setCode] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleVerify = async () => {
    if (!code.trim() || code.length !== 6) {
      setError('Please enter a 6-digit code');
      return;
    }
    setVerifying(true);
    setError(null);
    try {
      const { data: factors } = await supabase.auth.mfa.listFactors();
      if (factors?.totp?.length === 0) {
        setError('No MFA factor found. Please contact support.');
        return;
      }
      const factorId = factors.totp[0].id;

      const { data: challengeData, error: challengeError } = await supabase.auth.mfa.challenge({
        factorId
      });
      if (challengeError) throw challengeError;

      const { error: verifyError } = await supabase.auth.mfa.verify({
        factorId,
        challengeId: challengeData.id,
        code: code.trim()
      });
      if (verifyError) throw verifyError;

      setAuthState('checking_invitations');
      onVerified();
    } catch (e: any) {
      setError(e?.message || 'Invalid code. Please try again.');
      toast.error(e?.message || 'Verification failed');
    } finally {
      setVerifying(false);
    }
  };

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    setAuthState('unauthenticated');
  };

  return (
    <div className="flex items-center justify-center min-h-screen bg-[#F2F0EF] p-4">
      <div className="w-full max-w-sm space-y-6">
        <div className="text-center">
          <div className="mx-auto w-14 h-14 rounded-full bg-[#245F73]/10 flex items-center justify-center mb-4">
            <Shield className="h-7 w-7 text-[#245F73]" />
          </div>
          <h1 className="text-xl font-semibold">Two-Factor Authentication</h1>
          <p className="text-sm text-muted-foreground mt-2">
            Enter the 6-digit code from your authenticator app to continue
          </p>
        </div>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="mfa-code">Verification Code</Label>
            <Input
              id="mfa-code"
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              placeholder="000000"
              maxLength={6}
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
              onKeyDown={(e) => e.key === 'Enter' && handleVerify()}
              className="text-center text-lg tracking-[0.5em]"
              autoFocus
            />
          </div>
          {error && (
            <p className="text-sm text-red-600">{error}</p>
          )}
          <Button
            className="w-full"
            onClick={handleVerify}
            disabled={verifying || code.length !== 6}
          >
            {verifying ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Verifying...
              </>
            ) : (
              'Verify'
            )}
          </Button>
        </div>

        <div className="text-center">
          <button
            type="button"
            onClick={handleSignOut}
            className="text-sm text-muted-foreground hover:text-foreground"
          >
            Sign out and use a different account
          </button>
        </div>
      </div>
    </div>
  );
}
